import { useState } from 'react';

import { ExportDialog } from '@/components/ExportDialog';
import {
  ROSTER_CATEGORY_OPTIONS,
  rosterCategoryOf,
  rosterCategoryPatch,
} from '@/components/RosterFilters';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { InstituteClass } from '@/types/instituteClass';
import type { StudentListParams } from '@/types/student';
import { studentsApi, type StudentExportFilters } from '../api/students.api';
import { STUDENT_STATUS_LABEL, STUDENT_STATUS_ORDER } from '../constants';

/**
 * Every standing, not just the two the register's own filter offers: the
 * download is where the office asks for this year's leavers or alumni.
 */
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...STUDENT_STATUS_ORDER.map((status) => ({ value: status, label: STUDENT_STATUS_LABEL[status] })),
];

export interface StudentExportDialogProps {
  /** The register's filters right now — the dialog starts from them. */
  pageFilters: StudentListParams;
  classes: InstituteClass[];
  /** "2026-27" — names the file and heads the PDF. */
  sessionName: string;
  /** A college has no class or section to narrow by. */
  isCollege: boolean;
  /** "All houses", each house, then "Not assigned" — the register's own filter options. */
  houseOptions: { value: string; label: string }[];
  triggerClassName?: string;
}

/** The student register's Export: who, which details, Excel or PDF. */
export function StudentExportDialog({
  pageFilters,
  classes,
  sessionName,
  isCollege,
  houseOptions,
  triggerClassName,
}: StudentExportDialogProps) {
  const [filters, setFilters] = useState<StudentExportFilters>(() => fromPage(pageFilters));

  const patch = (values: Partial<StudentExportFilters>) =>
    setFilters((current) => ({ ...current, ...values }));

  const selectedClass = classes.find((one) => one.uuid === filters.classUuid);

  return (
    <ExportDialog
      title="Download student list"
      documentTitle={`Students ${sessionName}`}
      fileName={`students-${sessionName.replaceAll('/', '-')}`}
      noun={{ one: 'student', other: 'students' }}
      storageKey="export-fields:students"
      triggerClassName={triggerClassName}
      loadFields={studentsApi.exportFields}
      fetchRows={(fields) => studentsApi.exportRows(filters, fields)}
      onOpen={() => setFilters(fromPage(pageFilters))}
      filters={
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            label="Name or admission number"
            type="search"
            autoComplete="off"
            placeholder="Everyone"
            value={filters.search ?? ''}
            onChange={(event) => patch({ search: event.target.value })}
          />
          {!isCollege && (
            <>
              <Select
                label="Class"
                options={[
                  { value: '', label: 'All classes' },
                  ...classes.map((one) => ({ value: one.uuid, label: one.name })),
                ]}
                value={filters.classUuid ?? ''}
                // A section belongs to its class, so changing class clears it.
                onChange={(event) => patch({ classUuid: event.target.value, sectionUuid: '' })}
              />
              <Select
                label="Section"
                options={[
                  { value: '', label: 'All sections' },
                  ...(selectedClass?.sections ?? []).map((section) => ({
                    value: section.uuid,
                    label: section.name,
                  })),
                ]}
                disabled={!selectedClass}
                hint={selectedClass ? undefined : 'Choose a class first'}
                value={filters.sectionUuid ?? ''}
                onChange={(event) => patch({ sectionUuid: event.target.value })}
              />
            </>
          )}
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={filters.status ?? ''}
            onChange={(event) =>
              patch({ status: event.target.value as StudentExportFilters['status'] })
            }
          />
          <Select
            label="Category"
            options={ROSTER_CATEGORY_OPTIONS}
            value={rosterCategoryOf(filters)}
            onChange={(event) => patch(rosterCategoryPatch(event.target.value))}
          />
          <Select
            label="House"
            options={houseOptions}
            value={filters.houseUuid ?? ''}
            onChange={(event) => patch({ houseUuid: event.target.value })}
          />
        </div>
      }
    />
  );
}

/** The page's filters without the parts a download has no use for. */
function fromPage({
  page: _page,
  limit: _limit,
  attendanceMonth: _attendanceMonth,
  ...filters
}: StudentListParams): StudentExportFilters {
  return filters;
}
